import { TProductCategory, TProductInstance, TProductLocationPromotion } from '@lib/core/products/types';
import { selectRetailerTags } from '@lib/core/retailers/selectors/retailer';
import {
  selectAssociatedRetailerLocation,
  selectRetailerLocationId,
  selectRetailerLocationSlug,
} from '@lib/core/retailers/selectors/retailerLocation';
import { RETAILER_TAG_EXTERNAL_REFERENCE_ID } from '@lib/core/retailers/utils/consts';
import {
  BrowserUrlConsts,
  ENV_DYNAMIC_ENV_DOMAINS,
  ENV_MEDIA_DOMAIN,
  ENV_TEST_APP_DOMAIN,
  ENV_TEST_KIOSK_DOMAIN,
  SLOW_CONNECTION_COUNTER_KEY,
} from '@lib/core/service/consts';
import { selectServiceProductCategory } from '@lib/core/service/selectors';
import { selectRouteBasename } from '@lib/core/service/selectors/routes';
import { store } from '@lib/core/service/store';
import { TProductRate } from '@lib/core/users/slices/productRate';
import { SDK_PROFILE_ID } from '@lib/core/users/utils/consts';
import { UTM_CAMPAIGN_KIOSK, UTM_MEDIUM_QR_CODE } from '@lib/tools/dat/consts';
import { currencies } from '@lib/tools/locale/utils/consts';
import { RouteUtils } from '@lib/tools/routes';
import { PRICE_RANGE } from '@lib/tools/shared/helpers/consts';
import { TTastePathData } from '@lib/tools/tastePathProducts/types/types';
import { PAGES } from '@lib/tools/views/urls';

import { TJourneyStep } from '@components/web/src/organisms/TastePath/TastePathProgress/TastePathProgress';
import { SLOW_PAGE_SCENARIOS } from '@components/web/src/templates/Loading/LoadingSpinner/LoadingSpinner';

export const uuidv4 = (): string => {
  return `${1e7}-${1e3}-${4e3}-${8e3}-${1e11}`.replace(/[018]/g, (c: any) =>
    (c ^ (window.crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16),
  );
};

/**
 * Returns a boolean true if the app is mounted inside an iframe
 */
export const isAppInIframe = window.self !== window.top;

// ? Improve core navigator when react-router fixes nav bugs
export const prependBasename = (page, params = {}): string => {
  const baseUrl = selectRouteBasename(store.getState());
  const searchParams = new URLSearchParams(params).toString();
  const paramsString = searchParams ? `?${searchParams}` : '';

  return baseUrl + page + paramsString;
};

/**
 * @deprecated
 */

export function stringToBoolean(value: string) {
  return (
    value !== 'false' &&
    value !== 'undefined' &&
    value !== '0' &&
    value !== 'NaN' &&
    value !== 'null' &&
    value !== undefined &&
    !!value.replace(/\s/g, '').length
  );
}

export const capitalizeFirstLetter = (value: string) => (value ? value.charAt(0)?.toUpperCase() + value.slice(1) : '');

export const getVinhoodTagsFromCharacteristics = characteristics => {
  if (characteristics) {
    const vinhoodTagsArray = [];

    characteristics.forEach(({ image, name }) => {
      let icon = '';

      if (image && image.trim() !== '') {
        icon = image;
      } else {
        try {
          icon = require(`media/images/vinhood/b2c/tags/tag_${name?.toLowerCase()}.svg`);
        } catch (error) {
          icon = '';
        }
      }
      vinhoodTagsArray.push({ icon, tagName: name });
    });
    return vinhoodTagsArray;
  }
  return null;
};

export const convertImageFiletoBase64 = (file: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as any);
    reader.onerror = err => reject(err);
  });

export const getMultipleUniqueRandomItemsFromArray = (arr = [], count) => {
  if (arr.length < 1) return [];
  const shuffled = [...arr].sort(() => 0.5 - Math.random());

  return shuffled.slice(0, count);
};

export const getRandomItemFromArray = (array: any[]) => {
  return array[Math.floor(Math.random() * array.length)];
};

export const shuffleArray = array => [...array].sort(() => Math.random() - 0.5);

export const shuffleProductsInsideRankGroups = (array: TProductInstance[]): TProductInstance[] => {
  const resultArray = [];
  const alreadyShuffledRanks = [];
  array?.forEach(product => {
    if (!alreadyShuffledRanks.includes(product.rank)) {
      const newRankPart = array.filter(({ rank }) => rank === product.rank);
      resultArray.push(...shuffleArray(newRankPart));
      alreadyShuffledRanks.push(product.rank);
    }
  });
  return resultArray;
};

// later it will be moved to RouteUtils
export const createMobileKioskQRUrl = (params?: Record<string, string | undefined>) => {
  const { origin: originalOrigin, pathname } = window.location;
  const state = store.getState();
  const baseUrl = selectRouteBasename(state) ?? '';
  const retailerLocationId = selectRetailerLocationId(state) ?? '';
  const associatedRetailerLocationId = selectAssociatedRetailerLocation(state)?.identifier ?? '';

  const productId = params?.[BrowserUrlConsts.productId];
  const shouldRedirectToProductPage = params?.[BrowserUrlConsts.shouldRedirectToProductPage] === 'true';

  const origin = originalOrigin.replace('kiosk', 'app');
  let mobileKioskPathname = pathname.replace(retailerLocationId, associatedRetailerLocationId);

  if (shouldRedirectToProductPage && productId) {
    mobileKioskPathname = `${baseUrl}${PAGES.vinhood.product}`.replace(
      retailerLocationId,
      associatedRetailerLocationId,
    );
  }

  const queryParams: Record<string, string> = {
    utm_campaign: UTM_CAMPAIGN_KIOSK,
    utm_content: selectServiceProductCategory(state),
    utm_medium: UTM_MEDIUM_QR_CODE,
    utm_source: selectRetailerLocationSlug(state),
    utm_term: RouteUtils.getPage(),

    [BrowserUrlConsts.isFromKioskQr]: 'true',
    ...params,
  };

  return new URL(`${origin}${mobileKioskPathname}?${new URLSearchParams(queryParams)}`).toString();
};

export const promotionsFilter = (promotions: TProductLocationPromotion[] = [], promotionsSlugsToFilter: string[]) =>
  promotions?.filter(promotionData => promotionsSlugsToFilter.includes(promotionData?.typeSlug)) || [];

export const migrateAppSliceToServiceSlice = () => {
  const legacyKey = 'persist:app';
  const newKey = 'persist:service';

  const migrateState = () => {
    const legacyState = localStorage.getItem(legacyKey);
    if (legacyState) {
      localStorage.setItem(newKey, legacyState);
      localStorage.removeItem(legacyKey);
    }
  };

  migrateState();
};

export const createTasteJourneyData = ({
  tastePathData,
  productRateData,
  currentStyleCategoryId,
  retailerMaxLengthTastePathPerCategory,
}: {
  tastePathData: Record<string, TTastePathData>;
  productRateData: TProductRate[];
  currentStyleCategoryId: string;
  retailerMaxLengthTastePathPerCategory: number;
}): TJourneyStep[] => {
  return Object.entries(tastePathData).map(([styleId, tasteData]) => {
    const isDone = productRateData.filter(product => product?.product?.character?.type?.identifier === styleId).length;

    const currentTastePathCategoryIdProductsLength = tasteData?.products.length;
    const maxSubCategoryLength =
      currentTastePathCategoryIdProductsLength >= retailerMaxLengthTastePathPerCategory
        ? retailerMaxLengthTastePathPerCategory
        : currentTastePathCategoryIdProductsLength;

    const category = tastePathData[styleId]?.name;

    const subCategory = Array.from({ length: maxSubCategoryLength }, (_, i) => ({
      id: i + 1,
      isActive: currentStyleCategoryId === styleId && i + 1 === isDone + 1,
      isDone: i + 1 <= isDone,
    }));

    return (
      currentTastePathCategoryIdProductsLength && {
        category,
        subCategory,
      }
    );
  });
};

export const testConnectionQuality = async () => {
  const loadingTimeFlag = 300;
  return new Promise<keyof typeof SLOW_PAGE_SCENARIOS>(resolve => {
    const image = new Image();
    const startTime = new Date().getTime();
    let isResolved = false;

    const timeout = setTimeout(() => {
      if (!isResolved) {
        resolve(SLOW_PAGE_SCENARIOS.isLowSpeedConnection);
        isResolved = true;
      }
    }, loadingTimeFlag);

    image.onload = () => {
      const elapsed = new Date().getTime() - startTime;
      console.warn('Elapsed time:', elapsed);
      if (!isResolved) {
        clearTimeout(timeout);
        isResolved = true;
        if (elapsed > loadingTimeFlag) {
          resolve(SLOW_PAGE_SCENARIOS.isLowSpeedConnection);
        } else {
          resolve(SLOW_PAGE_SCENARIOS.isLowSpeedAPI);
        }
      }
    };

    image.onerror = () => {
      if (!isResolved) {
        clearTimeout(timeout);
        isResolved = true;
        resolve(SLOW_PAGE_SCENARIOS.isLowSpeedConnection);
      }
    };

    image.src = `${ENV_MEDIA_DOMAIN}/logos/taste-experience-logo.png?cacheBust=${new Date().getTime()}`;
  });
};

export const createPriceRangeSymbolValues = ({
  minValue,
  maxValue,
  productCategory,
  symbol = currencies.EUR,
}: {
  minValue?: number;
  maxValue?: number;
  productCategory: TProductCategory;
  symbol?: string;
}): string => {
  const range = PRICE_RANGE[productCategory] ?? [];

  const findLevel = (value?: number) =>
    value
      ? (range.find(({ min, max, isMax }) => (isMax ? value >= min : value >= min && value <= (max ?? Infinity)))
          ?.level ?? null)
      : null;

  const from = findLevel(minValue);
  const to = findLevel(maxValue);

  if (from && to) return from === to ? symbol.repeat(from) : `${symbol.repeat(from)} - ${symbol.repeat(to)}`;
  if (from) return symbol.repeat(from);
  if (to) return symbol.repeat(to);

  return '';
};

/**
 * Returns true if running on localhost.
 */
export const isLocalhost = (): boolean => {
  if (typeof window === 'undefined') return false;
  return ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
};

/**
 * Returns true if running in a dynamic environment, as defined by the env variable
 * or if the hostname includes any of the fallback domains.
 * Set REACT_APP_DYNAMIC_ENV_DOMAINS as a comma-separated list (e.g. ".web.vinhood.dev,.kiosk.vinhood.dev").
 */
export const isDynamicEnv = (): boolean => {
  if (typeof window === 'undefined') return false;

  const envDomains = ENV_DYNAMIC_ENV_DOMAINS;
  const fallbackDomains = envDomains
    ? envDomains
        .split(',')
        .map(d => d.trim())
        .filter(Boolean)
    : [ENV_TEST_APP_DOMAIN, ENV_TEST_KIOSK_DOMAIN];
  return fallbackDomains.some(domain => window.location.hostname.includes(domain));
};

export const getSavedParam = (paramName: string) => {
  const value = localStorage.getItem(paramName);
  if (value) {
    return { name: paramName, value };
  }
  return null;
};
export const getExternalReferenceParam = () => {
  const state = store.getState();
  const retailerTags = selectRetailerTags(state);
  const externalReferenceIdTag = retailerTags?.[RETAILER_TAG_EXTERNAL_REFERENCE_ID];
  return getSavedParam(externalReferenceIdTag);
};

export const normalizeRoute = (route: string) => {
  if (!route) return '/';
  let result = route.trim();

  if (!result.startsWith('/')) result = `/${result}`;
  if (!result.endsWith('/')) result += '/';

  return result;
};

export const SlowConnectionCounter = {
  get(): number {
    return Number(localStorage.getItem(SLOW_CONNECTION_COUNTER_KEY)) || 0;
  },
  increment(): void {
    const count = this.get();
    localStorage.setItem(SLOW_CONNECTION_COUNTER_KEY, String(count + 1));
  },
  reset(): void {
    localStorage.removeItem(SLOW_CONNECTION_COUNTER_KEY);
  },
};

export const hasWordLongerThanLimit = (text: unknown, maxWordLength: number) =>
  typeof text === 'string' && text.split(' ').some(word => word.length > maxWordLength);

export const readSdkProfileIdFromQuery = (): string | null => {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get(SDK_PROFILE_ID);
};
